import { useCallback, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";
import {
  BASIC_DARK_GRAY,
  BASIC_ORANGE,
  BASIC_WHITE,
} from "../../../constants/colors";
import { appActions } from "../../../ducks/app";
import { Menu } from "../../../ducks/menu";
import { MenuImage } from "../../molecules/menu/MenuImage";
import { MenuPrice } from "../../molecules/menu/MenuPrice";

type Props = {
  menu: Menu;
  selectedLimitedMenus: Menu[];
  onFavorite: (
    menuCode: string,
    favorite: boolean,
    onSuccess?: () => void,
  ) => void;
  onSelectMenu: (args: {
    amount: number;
    optionId?: number;
    optionName?: string;
  }) => void;
  noPopup?: boolean;
  disableClick?: boolean;
  isSoldOut?: boolean;
  isUserLimitReached?: boolean;
};

export const MenuItem: React.FC<Props> = ({
  menu,
  selectedLimitedMenus,
  onFavorite,
  onSelectMenu,
  noPopup,
  disableClick,
  isSoldOut,
  isUserLimitReached,
}) => {
  const dispatch = useDispatch();

  const [isMenuClicked, setIsMenuClicked] = useState<boolean>(false);

  const [count, setCount] = useState(1);
  const [selectedOption, setSelectedOption] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [animate, setAnimate] = useState(false);

  const shouldDisable = useMemo(() => {
    return menu.isPreorder && !menu.isEnableButton;
  }, [menu]);

  const handleAnimation = useCallback(() => {
    setAnimate(true);
    setTimeout(() => setAnimate(false), 1000);
  }, []);

  const handleAddToCart = useCallback(
    (amount: number, optionId?: number, optionName?: string) => {
      if (amount === 0) return;

      if (!isMenuClicked || !menu.isFavorite) {
        handleAnimation();
      } else {
        const animationTrigger = document.getElementById(
          `menu-item-animation-trigger-${menu.id}`,
        );
        if (animationTrigger) animationTrigger.click();
      }

      onSelectMenu({
        amount,
        optionId,
        optionName,
      });
    },
    [isMenuClicked, menu],
  );

  const handleClickMenuItem = useCallback(
    (isClickEvent?: boolean) => {
      if (disableClick) return;

      if (isClickEvent) setIsMenuClicked(true);

      dispatch(
        appActions.showMenuDetailPopup.started({
          menu,
          isLimitedMenuSelected,
          onFavorite: (favorite) => {
            onFavorite(menu.code, favorite, () => {
              dispatch(appActions.setMenuDetailPopupFavorite(favorite));
            });
          },
          onSelectMenu: handleAddToCart,
          onClose: () => dispatch(appActions.closeMenuDetailPopup()),
          isSoldOut: isSoldOut,
          isUserLimitReached: isUserLimitReached,
        }),
      );
    },
    [menu, isSoldOut, isUserLimitReached],
  );

  const isLimitedMenuSelected = useMemo(() => {
    if (!menu.isLimitedMenu) return false;

    return selectedLimitedMenus.some(
      (selectedMenu) => selectedMenu.code === menu.code,
    );
  }, [selectedLimitedMenus]);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const handleFavorite = (favorite: boolean) => {
    onFavorite(menu.code, favorite, () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menuRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        });
      });
    });
  };

  return (
    <>
      <div
        id={noPopup ? `menu-item-animation-trigger-${menu.id}` : undefined}
        style={{ display: "none" }}
        onClick={handleAnimation}
      ></div>

      <MenuItemContainer ref={menuRef}>
        <MenuImage
          id={menu.id}
          imageUrl={menu.imageUrl}
          isFavorite={menu.isFavorite}
          onFavorite={handleFavorite}
          onClickImage={() => handleClickMenuItem(noPopup)}
        />

        <MenuNameContainer>
          <MenuName>{menu.name}</MenuName>
        </MenuNameContainer>

        <MenuPriceContainer>
          {(isSoldOut || isUserLimitReached) && (
            <MenuSoldOutButton>
              <MenuSoldOutButtonText>
                {isSoldOut ? "SOLD OUT" : "注文できません"}
              </MenuSoldOutButtonText>
            </MenuSoldOutButton>
          )}
          {!isSoldOut && !isUserLimitReached && (
            <MenuPrice
              price={menu.price}
              priceWithTax={menu.priceWithTax}
              isOriginalPrice={!!menu.campaign}
              size="md"
            />
          )}
        </MenuPriceContainer>

        {menu.campaign && (
          <>
            <MenuPriceContainer>
              <MenuPrice
                price={menu.campaign.price}
                priceWithTax={menu.campaign.priceWithTax}
                isDiscountPrice
                size="md"
              />
            </MenuPriceContainer>
            <MenuCampaignDetail>{menu.campaign.details}</MenuCampaignDetail>
          </>
        )}
      </MenuItemContainer>
    </>
  );
};

const MenuItemContainer = styled.div`
  display: flex;
  overflow: hidden;
  flex-direction: column;
  align-items: center;
  text-align: left;
  margin: 0;
  color: ${BASIC_DARK_GRAY};
  background-color: ${BASIC_WHITE};
  border-radius: 8px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.16);
`;

const MenuNameContainer = styled.div`
  width: 100%;
  text-align: left;
  padding: 8px 14px;
  box-sizing: border-box;
`;

const MenuName = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  line-height: 1.3;
`;

const MenuPriceContainer = styled.div`
  width: 100%;
  align-self: flex-start;
  text-align: left;
  box-sizing: border-box;
  padding: 0 14px 10px 14px;
`;

const MenuCampaignDetail = styled.div`
  text-align: left;
  font-size: 14px;
  color: ${BASIC_ORANGE};
  font-weight: bold;
  line-height: 1.3;
  padding: 0 14px 10px 14px;
  margin-top: -6px;
  align-self: flex-start;
`;

const MenuSoldOutButton = styled.div`
  display: flex;
  padding: 2px 0;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  border-radius: 4px;
  background: #999;
`;

const MenuSoldOutButtonText = styled.p`
  color: #fff;
  text-align: center;
  font-family: "Hiragino Sans";
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.025em;
`;
